import React, { useMemo } from "react";
import DOMPurify from "dompurify";
import { Copy, Check, FileCode, AlertTriangle } from "lucide-react";
import { cx } from "../../lib/cx";

// Strict DOMPurify config for the syntax-highlighter output. Only allows
// the <span class="..."> markers we inject ourselves; strips everything
// else (event handlers, other tags, attributes) so attacker-controlled
// scanner findings can't smuggle markup through the snippet preview.
const SANITIZE_CONFIG: DOMPurify.Config = {
  ALLOWED_TAGS: ["span"],
  ALLOWED_ATTR: ["class"],
  ALLOW_DATA_ATTR: false,
  KEEP_CONTENT: true,
};

// Permit only the colour classes the highlighter actually emits. Anything
// else is stripped from the class attribute. DOMPurify already blocks
// event handlers and `javascript:` URIs by default; this hook ensures
// the `class` attribute can't carry attacker-controlled values either.
const ALLOWED_CLASS_PATTERN =
  /^text-(?:purple|green|gray|amber|cyan)-(?:300|400|500)(?: italic)?$/;

DOMPurify.addHook("afterSanitizeAttributes", (node) => {
  if (node.nodeName !== "SPAN") return;
  const cls = node.getAttribute("class");
  if (cls && !ALLOWED_CLASS_PATTERN.test(cls)) {
    node.removeAttribute("class");
  }
});

interface CodeSnippetProps {
  code: string;
  language?: string;
  filename?: string;
  startLine?: number;
  highlightLines?: number[];  // Lines to highlight (vulnerable lines)
  maxHeight?: string;
  className?: string;
  showLineNumbers?: boolean;
  title?: string;
}

// Simple syntax highlighting for common patterns
function highlightCode(code: string, language?: string): string {
  if (!code) return "";

  // Strip null bytes to prevent placeholder collision attacks (XSS defense)
  code = code.replace(/\x00/g, "");

  // Escape HTML first
  let html = code
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
  
  // Apply syntax highlighting based on language
  const lang = (language || "").toLowerCase();
  
  if (["javascript", "typescript", "js", "ts", "jsx", "tsx"].includes(lang)) {
    // Keywords
    html = html.replace(
      /\b(const|let|var|function|return|if|else|for|while|do|switch|case|break|continue|new|try|catch|throw|async|await|class|extends|import|export|from|default)\b/g,
      '<span class="text-purple-400">$1</span>'
    );
    // Strings
    html = html.replace(
      /(["'`])(?:(?!\1)[^\\]|\\.)*?\1/g,
      '<span class="text-green-400">$&</span>'
    );
    // Comments
    html = html.replace(
      /(\/\/.*$|\/\*[\s\S]*?\*\/)/gm,
      '<span class="text-gray-500 italic">$1</span>'
    );
    // Numbers
    html = html.replace(
      /\b(\d+\.?\d*)\b/g,
      '<span class="text-amber-400">$1</span>'
    );
  } else if (["python", "py"].includes(lang)) {
    // Keywords
    html = html.replace(
      /\b(def|class|return|if|elif|else|for|while|try|except|finally|with|as|import|from|raise|pass|break|continue|and|or|not|in|is|None|True|False|self|lambda|yield|global|nonlocal|async|await)\b/g,
      '<span class="text-purple-400">$1</span>'
    );
    // Strings
    html = html.replace(
      /("""[\s\S]*?"""|'''[\s\S]*?'''|"(?:[^"\\]|\\.)*"|'(?:[^'\\]|\\.)*')/g,
      '<span class="text-green-400">$&</span>'
    );
    // Comments
    html = html.replace(
      /(#.*$)/gm,
      '<span class="text-gray-500 italic">$1</span>'
    );
    // Numbers
    html = html.replace(
      /\b(\d+\.?\d*)\b/g,
      '<span class="text-amber-400">$1</span>'
    );
  } else if (["java", "csharp", "cs", "c", "cpp", "go"].includes(lang)) {
    // Keywords
    html = html.replace(
      /\b(public|private|protected|static|final|void|int|string|boolean|bool|char|double|float|long|short|byte|class|interface|extends|implements|new|return|if|else|for|while|do|switch|case|break|continue|try|catch|throw|throws|finally|import|package|null|true|false|this|super|func|var|const|type|struct|defer|go|chan|select|make|range|nil)\b/g,
      '<span class="text-purple-400">$1</span>'
    );
    // Strings
    html = html.replace(
      /(["'])(?:(?!\1)[^\\]|\\.)*?\1/g,
      '<span class="text-green-400">$&</span>'
    );
    // Comments
    html = html.replace(
      /(\/\/.*$|\/\*[\s\S]*?\*\/)/gm,
      '<span class="text-gray-500 italic">$1</span>'
    );
    // Numbers
    html = html.replace(
      /\b(\d+\.?\d*)\b/g,
      '<span class="text-amber-400">$1</span>'
    );
  } else if (["yaml", "yml", "dockerfile", "docker"].includes(lang)) {
    // Keys
    html = html.replace(
      /^(\s*[\w-]+):/gm,
      '<span class="text-cyan-400">$1</span>:'
    );
    // Strings
    html = html.replace(
      /(["'])(?:(?!\1)[^\\]|\\.)*?\1/g,
      '<span class="text-green-400">$&</span>'
    );
    // Comments
    html = html.replace(
      /(#.*$)/gm,
      '<span class="text-gray-500 italic">$1</span>'
    );
  } else if (["sql"].includes(lang)) {
    // Keywords (case-insensitive)
    html = html.replace(
      /\b(SELECT|FROM|WHERE|INSERT|UPDATE|DELETE|CREATE|ALTER|DROP|TABLE|INDEX|JOIN|LEFT|RIGHT|INNER|OUTER|ON|AND|OR|NOT|NULL|IS|IN|LIKE|ORDER|BY|GROUP|HAVING|LIMIT|OFFSET|AS|SET|VALUES|INTO)\b/gi,
      '<span class="text-purple-400">$1</span>'
    );
    // Strings
    html = html.replace(
      /(["'])(?:(?!\1)[^\\]|\\.)*?\1/g,
      '<span class="text-green-400">$&</span>'
    );
    // Comments
    html = html.replace(
      /(--.*$|\/\*[\s\S]*?\*\/)/gm,
      '<span class="text-gray-500 italic">$1</span>'
    );
  }
  
  // Sanitize via DOMPurify with a strict allowlist (span + class only).
  // Replaces a previous custom regex/placeholder approach that was hard
  // to audit and easy to bypass — DOMPurify is the OWASP-recommended
  // browser sanitiser and handles every edge case we used to hand-roll.
  return DOMPurify.sanitize(html, SANITIZE_CONFIG);
}

// Detect language from filename
function detectLanguage(filename?: string): string {
  if (!filename) return "";
  
  const ext = filename.split(".").pop()?.toLowerCase() || "";
  const langMap: Record<string, string> = {
    js: "javascript",
    jsx: "javascript",
    ts: "typescript",
    tsx: "typescript",
    py: "python",
    java: "java",
    cs: "csharp",
    c: "c",
    cpp: "cpp",
    h: "c",
    hpp: "cpp",
    go: "go",
    rb: "ruby",
    rs: "rust",
    php: "php",
    sql: "sql",
    yaml: "yaml",
    yml: "yaml",
    json: "json",
    xml: "xml",
    html: "html",
    css: "css",
    sh: "shell",
    bash: "shell",
    dockerfile: "dockerfile",
  };
  
  if (filename.toLowerCase().includes("dockerfile")) return "dockerfile";
  return langMap[ext] || ext;
}

export default function CodeSnippet({
  code,
  language,
  filename,
  startLine = 1,
  highlightLines = [],
  maxHeight = "400px",
  className,
  showLineNumbers = true,
  title,
}: CodeSnippetProps) {
  const [copied, setCopied] = React.useState(false);
  
  const detectedLang = language || detectLanguage(filename);
  
  const lines = useMemo(() => {
    if (!code) return [];
    return code.split("\n");
  }, [code]);
  
  const highlightedCode = useMemo(() => {
    return highlightCode(code, detectedLang);
  }, [code, detectedLang]);
  
  const highlightedLines = useMemo(() => {
    return highlightedCode.split("\n");
  }, [highlightedCode]);
  
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback
      const textarea = document.createElement("textarea");
      textarea.value = code;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };
  
  if (!code) {
    return (
      <div className={cx("rounded-lg border border-divider bg-surface1 p-4", className)}>
        <div className="flex items-center gap-2 text-muted text-sm">
          <AlertTriangle className="h-4 w-4" />
          <span>Code snippet not available</span>
        </div>
      </div>
    );
  }
  
  // Find the vulnerable line (first highlighted line or middle of snippet)
  const vulnerableLine = highlightLines.length > 0 
    ? highlightLines[0] 
    : startLine + Math.floor(lines.length / 2);
  
  return (
    <div className={cx("rounded-lg border border-divider bg-bg overflow-hidden", className)}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 bg-surface1 border-b border-divider">
        <div className="flex items-center gap-2">
          <FileCode className="h-4 w-4 text-muted" />
          {title ? (
            <span className="text-sm font-medium text-text">{title}</span>
          ) : filename ? (
            <span className="text-sm font-mono text-text">{filename}</span>
          ) : (
            <span className="text-sm text-muted">Code Snippet</span>
          )}
          {detectedLang && (
            <span className="text-xs px-2 py-0.5 rounded bg-surface2 text-muted">
              {detectedLang}
            </span>
          )}
        </div>
        <button
          onClick={handleCopy}
          className="flex items-center gap-1.5 px-2 py-1 rounded text-xs text-muted hover:text-text hover:bg-surface2 transition-colors"
          title="Copy code"
        >
          {copied ? (
            <>
              <Check className="h-3.5 w-3.5 text-success" />
              <span>Copied</span>
            </>
          ) : (
            <>
              <Copy className="h-3.5 w-3.5" />
              <span>Copy</span>
            </>
          )}
        </button>
      </div>
      
      {/* Code area */}
      <div 
        className="overflow-auto"
        style={{ maxHeight }}
      >
        <table className="w-full border-collapse">
          <tbody>
            {highlightedLines.map((line, index) => {
              const lineNumber = startLine + index;
              const isHighlighted = highlightLines.includes(lineNumber);
              const isVulnerable = lineNumber === vulnerableLine;
              
              return (
                <tr 
                  key={index}
                  className={cx(
                    "group",
                    isHighlighted && "bg-critical/10",
                    isVulnerable && "bg-critical/20"
                  )}
                >
                  {showLineNumbers && (
                    <td className={cx(
                      "select-none text-right pr-4 pl-4 py-0 text-xs font-mono border-r border-divider/50 w-[1%] whitespace-nowrap",
                      isHighlighted || isVulnerable ? "text-critical" : "text-muted/50",
                      isVulnerable && "font-bold"
                    )}>
                      {isVulnerable && (
                        <AlertTriangle className="inline h-3 w-3 mr-1 text-critical" />
                      )}
                      {lineNumber}
                    </td>
                  )}
                  <td className={cx(
                    "py-0 px-4 text-sm font-mono whitespace-pre",
                    isHighlighted || isVulnerable ? "text-text" : "text-text2"
                  )}>
                    <span dangerouslySetInnerHTML={{ __html: line || " " }} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      
      {/* Footer with line info */}
      {highlightLines.length > 0 && (
        <div className="px-4 py-2 bg-critical/10 border-t border-critical/30 text-xs text-critical flex items-center gap-2">
          <AlertTriangle className="h-3.5 w-3.5" />
          <span>
            Vulnerability detected on line{highlightLines.length > 1 ? "s" : ""}{" "}
            <strong>{highlightLines.join(", ")}</strong>
          </span>
        </div>
      )}
    </div>
  );
}

// Compact version for inline display
export function CodeSnippetInline({
  code,
  maxLength = 100,
  className,
}: {
  code: string;
  maxLength?: number;
  className?: string;
}) {
  if (!code) return null;
  
  const trimmed = code.trim();
  const display = trimmed.length > maxLength 
    ? trimmed.substring(0, maxLength) + "..."
    : trimmed;
  
  return (
    <code className={cx(
      "px-2 py-1 rounded bg-surface2 text-xs font-mono text-text2 break-all",
      className
    )}>
      {display}
    </code>
  );
}
